import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, FileText, ChevronDown, ChevronUp, ChevronRight,
} from 'lucide-react';
import StatCard from '../shared/StatCard';
import StatusFilterDropdown from '../shared/StatusFilterDropdown';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';
import DateRangeFilter from '../shared/DateRangeFilter';
import ProjectFilter from '../shared/ProjectFilter';
import RoleSummaryTab from '../shared/RoleSummaryTab';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, selectStyle } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL, PAYMENT_METHODS } from '../../../../shared/constants';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';

const FILTERS = [
  { value: 'all',                  label: 'Todos' },
  { value: 'pending',              label: 'Pendiente' },
  { value: 'partially_approved',   label: 'Parcial Aprobado' },
  { value: 'sent_back',            label: 'Devuelto' },
  { value: 'partially_returned',   label: 'Parcial Devuelto' },
  { value: 'pending_review',       label: 'Pendiente Revisión' },
  { value: 'approved',             label: 'Aprobado' },
  { value: 'paid',                 label: 'Pagado' },
  { value: 'rejected',             label: 'Rechazado' },
];

const STATUS_ORDER = ['pending', 'partially_approved', 'sent_back', 'partially_returned', 'pending_review', 'approved', 'paid', 'rejected'];

const SECTION_LABELS = {
  pending:              'En Revisión',
  partially_approved:   'Parcialmente Aprobado',
  sent_back:            'Devuelto',
  partially_returned:   'Parcialmente Devuelto',
  pending_review:       'Pendiente de Revisión',
  approved:             'Aprobado',
  paid:                 'Pagado',
  rejected:             'Rechazado',
};

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: '600',
      whiteSpace: 'nowrap',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function SectionHeader({ status, count, expanded, onToggle }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const Icon = expanded ? ChevronUp : ChevronDown;
  return (
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        background: '#fff',
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: expanded ? '12px 12px 0 0' : 12,
        cursor: 'pointer',
        fontFamily: 'Inter, sans-serif',
        minHeight: 48,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%',
          background: s.border, flexShrink: 0, display: 'inline-block',
        }} />
        <span style={{ fontWeight: '600', fontSize: 14, color: NAVY }}>
          {SECTION_LABELS[status] || status}
        </span>
      </div>
      <span style={{
        background: '#f1f5f9', color: '#64748b',
        borderRadius: 20, padding: '2px 10px',
        fontSize: 12, fontWeight: '500',
        marginRight: 12,
      }}>
        {count} reporte{count !== 1 ? 's' : ''}
      </span>
      <Icon size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
    </button>
  );
}

// Use the server-provided receiptStatus when available; fall back to deriving from items
function resolveGroupStatus(receiptStatus, items) {
  // Trust the server's receipt-level status — it reflects reviewer actions too
  if (receiptStatus) return receiptStatus;
  const statuses = items.map(i => i.status);
  if (statuses.length === 0) return 'pending';
  if (statuses.every(s => s === 'approved')) return 'pending_review';
  if (statuses.every(s => s === 'rejected')) return 'rejected';
  if (statuses.every(s => s === 'sent_back')) return 'sent_back';
  if (statuses.some(s => s === 'sent_back')) return 'partially_returned';
  if (statuses.some(s => s === 'rejected') && statuses.some(s => s === 'pending')) return 'partially_returned';
  if (statuses.some(s => s === 'rejected')) return 'rejected';
  if (statuses.some(s => s === 'approved') && statuses.some(s => s === 'pending')) return 'partially_approved';
  if (statuses.every(s => s === 'pending')) return 'pending';
  return 'pending';
}

export default function SupervisorListPage({ employees }) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [items, setItems]        = useState([]);
  const [filter, setFilter]      = useState(new Set());
  const [dateFrom, setDateFrom]  = useState(null);
  const [dateTo, setDateTo]      = useState(null);
  const [employeeId, setEmployeeId] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [sectionExpanded, setSectionExpanded] = useState({
    pending: true, partially_approved: true, sent_back: true, partially_returned: true, pending_review: false, approved: false, paid: false, rejected: false,
  });

  const fetchItems = useCallback(async (params = {}) => {
    try {
      const res = await client.get('/api/receipt-items/assigned', { params });
      setItems(res.data);
    } catch (err) {
      console.error('Failed to load items:', err);
    }
  }, []);

  useEffect(() => {
    const statusParam = filter.size > 0 ? [...filter].join(',') : undefined;
    fetchItems({ status: statusParam, dateFrom, dateTo, employeeId: employeeId || undefined });
  }, [fetchItems]);

  // Group items by receiptId
  const receiptGroups = useMemo(() => {
    const map = new Map();
    for (const item of items) {
      const rid = item.receiptId;
      if (!map.has(rid)) {
        map.set(rid, {
          receiptId: rid,
          vendor: item.vendor,
          employeeName: item.employeeName,
          receiptDate: item.receiptDate,
          receiptNumber: item.receiptNumber,
          invoiceNumber: item.invoiceNumber,
          fileName: item.fileName,
          fileType: item.fileType,
          previewUrl: item.previewUrl,
          paymentMethod: item.paymentMethod,
          receiptAmount: item.receiptAmount,
          receiptStatus: item.receiptStatus,
          receiptUserId: item.receiptUserId,
          items: [],
        });
      }
      map.get(rid).items.push(item);
    }
    let groups = Array.from(map.values()).map(g => ({
      ...g,
      totalAmount: g.items.reduce((s, i) => { const a=parseFloat(i.amount)||0; return s+a+a*(parseFloat(i.taxRate)||0)/100; }, 0),
      status: resolveGroupStatus(g.receiptStatus, g.items),
    }));
    // Apply project filter
    if (selectedProjects.length > 0) {
      groups = groups.filter(g => g.items.some(i => selectedProjects.includes(i.projectName)));
    }
    return groups;
  }, [items, selectedProjects]);

  // Derive unique project names from all items
  const allProjectNames = useMemo(() => {
    const names = new Set();
    items.forEach(i => { if (i.projectName) names.add(i.projectName); });
    return [...names].sort();
  }, [items]);

  function handleFilterChange(val) {
    setFilter(val);
    // Pass comma-separated statuses to API, or omit for all
    const statusParam = val.size > 0 ? [...val].join(',') : undefined;
    fetchItems({ status: statusParam, dateFrom, dateTo, employeeId: employeeId || undefined });
  }

  function handleDateChange({ dateFrom: df, dateTo: dt }) {
    setDateFrom(df);
    setDateTo(dt);
    const statusParam = filter.size > 0 ? [...filter].join(',') : undefined;
    fetchItems({ status: statusParam, dateFrom: df, dateTo: dt, employeeId: employeeId || undefined });
  }

  function handleEmployeeChange(val) {
    setEmployeeId(val);
    const statusParam = filter.size > 0 ? [...filter].join(',') : undefined;
    fetchItems({ status: statusParam, dateFrom, dateTo, employeeId: val || undefined });
  }

  function toggleSection(status) {
    setSectionExpanded(prev => ({ ...prev, [status]: !prev[status] }));
  }

  // Stats — count by receipt (group), not by individual line item
  const pendingReceipts   = receiptGroups.filter(g => g.status === 'pending' || g.status === 'partially_approved');
  const sentBackReceipts  = receiptGroups.filter(g => g.status === 'sent_back' || g.status === 'partially_returned');
  const approvedReceipts  = receiptGroups.filter(g => g.status === 'approved' || g.status === 'pending_review');
  const rejectedReceipts  = receiptGroups.filter(g => g.status === 'rejected');
  const approvedAmt       = approvedReceipts.reduce((s, g) => s + (g.totalAmount || 0), 0);

  // Receipt card — collapsed, click navigates to detail
  const renderReceiptCard = (group) => {
    const { receiptId, vendor, receiptDate, receiptNumber, invoiceNumber, previewUrl, fileType, paymentMethod, totalAmount, status: groupStatus, employeeName } = group;
    const projectNames = [...new Set(group.items.map(i => i.projectName).filter(Boolean))];

    return (
      <div
        key={receiptId}
        onClick={() => {
          // Own returned receipt → go to employee edit view for resubmit
          const isOwnReturn = group.receiptUserId === currentUser?.id
            && (group.status === 'sent_back' || group.status === 'partially_returned');
          navigate(isOwnReturn ? `/employee/receipt/${receiptId}` : `/supervisor/receipt/${receiptId}`);
        }}
        style={{
          ...bubbleStyle,
          padding: '14px 18px',
          cursor: 'pointer',
          transition: 'box-shadow 0.15s, transform 0.1s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '';
          e.currentTarget.style.transform = '';
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          {/* Preview thumbnail */}
          <div style={{
            width: 48, height: 48, borderRadius: 8, overflow: 'hidden',
            flexShrink: 0, background: '#f1f5f9', border: `1px solid ${BORDER_COLOR}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {previewUrl && fileType?.startsWith('image/') ? (
              <AuthedReceiptPreview src={previewUrl} fileType={fileType} alt="receipt" imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} style={{ width: '100%', height: '100%' }} />
            ) : (
              <FileText size={20} style={{ color: '#94a3b8' }} />
            )}
          </div>

          {/* Receipt info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '700', fontSize: 15, color: '#1e293b' }}>
                {receiptNumber ? `#${receiptNumber}` : '(Sin #)'}
                {invoiceNumber ? ` · Factura: ${invoiceNumber}` : ''}
                {' — '}
                {vendor || '(Sin proveedor)'}
              </span>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>|</span>
              <span style={{ fontSize: 13, color: '#64748b' }}>{receiptDate || '—'}</span>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>|</span>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {paymentMethod ? (PAYMENT_METHODS[paymentMethod] || paymentMethod) : '—'}
              </span>
              <StatusBadge status={groupStatus} />
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {employeeName && <span style={{ marginRight: 8 }}>{employeeName}</span>}
              {projectNames.length > 0
                ? `Proyectos: ${projectNames.join(', ')}`
                : 'Sin proyecto asignado'}
            </div>
          </div>

          {/* Total + chevron */}
          <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontWeight: '700', fontSize: 18, color: '#1e293b' }}>{fmt(totalAmount)}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{group.items.length} item{group.items.length !== 1 ? 's' : ''}</div>
            </div>
            <ChevronRight size={18} style={{ color: '#94a3b8' }} />
          </div>
        </div>
      </div>
    );
  };

  // Grouped view (filter === 'all')
  const renderGroupedView = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {STATUS_ORDER.map(status => {
        const sectionGroups = receiptGroups.filter(g => g.status === status);
        if (sectionGroups.length === 0) return null;
        const isExpanded = sectionExpanded[status];
        return (
          <div key={status}>
            <SectionHeader
              status={status}
              count={sectionGroups.length}
              expanded={isExpanded}
              onToggle={() => toggleSection(status)}
            />
            {isExpanded && (
              <div style={{
                border: `1px solid ${BORDER_COLOR}`,
                borderTop: 'none',
                borderRadius: '0 0 12px 12px',
                overflow: 'hidden',
                background: '#f8fafc',
                display: 'flex', flexDirection: 'column', gap: 12,
                padding: 12,
              }}>
                {sectionGroups.map(group => renderReceiptCard(group))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Flat view (specific filter)
  const renderFlatView = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {receiptGroups.map(group => renderReceiptCard(group))}
    </div>
  );

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 18px' }}>
        Panel de Revisión de Gastos
      </h2>

      {/* Stats */}
      <div className="stat-grid" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total"      value={receiptGroups.length}      onClick={() => handleFilterChange(new Set())}              active={filter.size === 0} />
        <StatCard label="Pendiente"  value={pendingReceipts.length}   onClick={() => handleFilterChange(new Set(['pending']))}   active={filter.size === 1 && filter.has('pending')} />
        <StatCard label="Devuelto"   value={sentBackReceipts.length}  onClick={() => handleFilterChange(new Set(['sent_back']))} active={filter.size === 1 && filter.has('sent_back')} />
        <StatCard label="Aprobado"   value={approvedReceipts.length}  subtitle={fmt(approvedAmt)} subtitleLarge highlight onClick={() => handleFilterChange(new Set(['approved']))} active={filter.size === 1 && filter.has('approved')} />
        <StatCard label="Rechazado"  value={rejectedReceipts.length}  onClick={() => handleFilterChange(new Set(['rejected']))}  active={filter.size === 1 && filter.has('rejected')} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <StatusFilterDropdown options={FILTERS} active={filter} onChange={handleFilterChange} />
        <div className="date-range" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} />
        </div>
        {employees.length > 0 && (
          <select
            value={employeeId}
            onChange={(e) => handleEmployeeChange(e.target.value)}
            style={{
              ...selectStyle, width: 'auto', minWidth: 160,
              height: 34, paddingTop: 0, paddingBottom: 0, fontSize: 13,
            }}
          >
            <option value="">Todos los Choferes</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        )}
        <ProjectFilter projects={allProjectNames} selected={selectedProjects} onChange={setSelectedProjects} />
      </div>

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <ClipboardList size={44} style={{ marginBottom: 12, color: '#cbd5e1' }} />
          <div style={{ fontSize: 16, fontWeight: '600', color: '#64748b' }}>No hay items asignados para revisar</div>
        </div>
      ) : filter.size === 0 ? renderGroupedView() : renderFlatView()}
    </div>
  );
}
